
import Button, { ButtonProps } from '@mui/material/Button'
import icons from '../assets/icons'
import StyledIcon from './StyledIcon'
import { Box, useTheme } from '@mui/material'
import { MaterialColor } from '../theme/default'
import { alphaHex } from '../../shared-solo/lib/utils'
import LoadingDots from './loadingDots'
import { createOutlineStyle } from '../lib/polyFills'

export interface RectButtonProps extends Omit<ButtonProps, 'size' | 'variant' | 'disableFocusRipple' | 'disableElevation'> {
  startIcon8?: keyof typeof icons
  endIcon8?: keyof typeof icons
  variant?: 'contained' | 'outlined'
  submitting?: boolean
  size?: 'tiny' | 'small' | 'medium' | 'large'
  iconSize?: keyof typeof iconSizes
  justifyContent?: 'flex-start' | 'center'
  target?: string
  padding?: 'default' | 'plump' | 'chunky'
}

export const iconSizes = {
  nano: 14,
  tiny: 20,
  small: 24,
  medium: 30,
  large: 34,
}

const fontSize = {
  tiny: '0.75em',
  small: '0.875em',
  medium: '1em',
  large: '1.1em',
}

const spacing = {
  tiny: 1,
  small: 1.5,
  medium: 2,
  large: 2,
}

export default function RectButton(props: RectButtonProps) {
  const theme = useTheme()

  const { color = 'primary' as ButtonProps['color'], variant = 'contained',
    sx, startIcon8, startIcon, endIcon8, endIcon, padding = 'default', submitting, iconSize, justifyContent = 'center', ...rest } = props

  const iconSizeKey = iconSize || props.size || 'medium'
  const icon8Size = iconSizes[iconSizeKey]

  let colorKey = color!
  if (colorKey === 'inherit') colorKey = 'primary'

  const icon8Color = props.disabled
    ? theme.palette.grey[400]
    : variant === 'contained'
      ? theme.palette[colorKey].contrastText
      : theme.palette[colorKey].main

  const submittingBackgroundColor = variant === 'contained'
    ? theme.palette[colorKey].main
    : theme.palette[colorKey].contrastText

  const sizeKey = props.size || 'medium'

  const paddingMultiplier = {
    default: 1,
    plump: 1.5,
    chunky: 2,
  }[padding]

  return <Button
    {...rest}
    color={color}
    sx={{
      overflow: 'visible',
      textAlign: 'left',
      lineHeight: 1.3,
      pointerEvents: submitting ? 'none' : 'auto',
      transition: 'all 0.3s ease-in-out',
      border: variant === 'contained' ? '1px solid rgba(255,255,255,0.1)' : '1px solid',
      backgroundColor: variant === 'outlined' ? theme.palette[colorKey].light + alphaHex(0.05) : undefined,
      boxShadow: variant === 'contained' && !submitting
        ? (theme) => `0px 0px 5px 2px ${theme.palette[colorKey].main}${alphaHex(0.15)}`
        : 'none',
      '&:hover': {
        '@media (hover: hover)': { // This only applies the hover styles if :hover is supported, e.g. not on touch devices
          boxShadow: (theme) => `0px 0px 0px 3px ${theme.palette[colorKey].main}${alphaHex(0.15)}`,
          backgroundColor: variant === 'contained'
            ? (theme) => theme.palette[colorKey].main
            : 'transparent',
        },
      },
      '&:active': {
        transition: 'none',
        backgroundColor: variant === 'contained'
          ? (theme) => theme.palette[colorKey].light
          : theme.palette[colorKey].light + alphaHex(0.05),
      },
      ...(variant === 'contained' && color === 'secondary' && createOutlineStyle(1, theme.palette[colorKey].dark)),
      fontSize: fontSize[sizeKey],
      paddingY: (theme) => theme.spacing(spacing[sizeKey] * (paddingMultiplier * 1)),
      paddingX: (theme) => theme.spacing(spacing[sizeKey] * (paddingMultiplier * 1)),
      gap: (theme) => theme.spacing(spacing[sizeKey] * (paddingMultiplier * 0.5)),
      justifyContent,
      ...sx,
    }}
    variant={variant}
    disableRipple
    startIcon={startIcon8 && !submitting
      ? <StyledIcon name={startIcon8}
        color={icon8Color as MaterialColor}
        sx={{
          mr: -1,
          my: -1,
        }}
        size={icon8Size} />
      : startIcon
    }
    endIcon={endIcon8 && !submitting
      ? <StyledIcon name={endIcon8}
        color={icon8Color as MaterialColor}
        sx={{ ml: -1, my: -1 }}
        size={icon8Size} />
      : endIcon
    }
    size={props.size as ButtonProps['size']}
  >
    {props.children}

    {submitting &&
      <Box sx={{
        position: 'absolute',
        backgroundColor: submittingBackgroundColor,
        borderRadius: 'inherit',
        top: 1, left: 1, right: 1, bottom: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <LoadingDots color={icon8Color as MaterialColor} size={icon8Size} />
      </Box>}
  </Button>
}